/* eslint-disable @typescript-eslint/no-empty-function */
import * as amplitude from '@amplitude/analytics-browser';
import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { User } from '@firsttable/types';
import { changeUser } from '@braze/web-sdk';
import SilverstripeContext from '@/components/context/Silverstripe';
import { useAuthState } from '@/components/context/AuthContext';
import {
  isBrazeAllowed,
  isBrazeInitialized,
  isPushNotificationGranted,
} from '@/lib/analytics/braze';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';
import {
  identifyAmplitudeUser,
  runConsentedAmplitudeOperation,
  trackAmplitudeEvent,
} from '@/lib/analytics/amplitude/client';
import { useConsent } from '@/lib/consent';
import { snaptrEvent, SnaptrUserData } from '@/lib/analytics/snapchat/snaptrEvent';
import { gtmEvent } from '@/lib/analytics/gtm/dataLayer';
import { openAIAdsMeasure } from '@/lib/analytics/openaiAds/openAIAdsMeasure';
import { podscribePurchase } from '@/lib/analytics/podscribe/events';
import { adalyserSignUp } from '@/lib/analytics/adalyser/events';
import { ANALYTICS_HIDDEN_PARAMETER_KEYS } from '@/lib/analytics/hiddenParameterKeys';
import {
  metaPixelCompleteRegistration,
  metaPixelPageView,
  metaPixelTrack,
} from '@/lib/analytics/metaPixel/metaPixelEvent';
import { TrackContext } from '../context/AnalyticsContext/AnalyticsContext';
import { notifyAnalyticsEventListeners } from '../utils/analyticsEventBus';

const getCookie = (cookieName: string) => {
  if (typeof window === 'undefined') return null;

  const cookies = document.cookie.split(';');
  const cookie = cookies.find((c) => c.trim().startsWith(`${cookieName}=`));

  if (!cookie) return null;

  const cookieValue = cookie.split('=')[1];
  return cookieValue;
};

const hideParameters = (props) => {
  const modifiedProps = { ...props };

  for (const propToHide of ANALYTICS_HIDDEN_PARAMETER_KEYS) {
    if (Object.prototype.hasOwnProperty.call(modifiedProps, propToHide)) {
      modifiedProps[propToHide] = 'HIDDEN';
    }
  }
  return modifiedProps;
};

const addCommonEvents = (props, user, canTrack) => {
  const fbp = canTrack ? getCookie('_fbp') : null;
  const fbc = canTrack ? getCookie('_fbc') : null;
  const eventSourceUrl = canTrack && typeof window !== 'undefined' ? window.location.href : null;
  const siteId = Number(process.env.NEXT_PUBLIC_SITE_ID);
  /**
   * nextLoyaltyTier tracks the total number of points when a user is not in the Platinum tier, when a user is in Platinum then nextLoyaltyTier is undefined and currentLoyaltyTier tracks the total number of points
   */
  const pointsEarned =
    user?.nextLoyaltyTier?.pointsEarned ?? user?.currentLoyaltyTier?.pointsEarned;

  const getCreditSpentAttributes = () => {
    if (!user?.activatedCreditsSum) return {};

    try {
      const creditSpentByCurrency: Record<string, number> = JSON.parse(user.activatedCreditsSum);
      const currencyAttrs: Record<string, number> = {};

      Object.entries(creditSpentByCurrency).forEach(([currency, amount]) => {
        if (amount > 0) {
          currencyAttrs[`user_total_credit_spent_${currency.toLowerCase()}`] = amount;
        }
      });

      return currencyAttrs;
    } catch (error) {
      const parsedError =
        error instanceof Error ? error : new Error('Failed to parse activatedCreditsSum');
      reportErrorToNewRelic(parsedError, { originalError: String(error) });
      return {};
    }
  };

  const modifiedProps: any = {
    ...props,
    action_source: 'website',
    ...(Number.isFinite(siteId) && { site_id: siteId }),
    ...(canTrack && {
      /** tiktok ads parameters */
      event_id: uuidv4(),
      /** meta ads parameters */
      ...(eventSourceUrl && { event_source_url: eventSourceUrl }),
      ...(fbp && { fbp }),
      ...(fbc && { fbc }),
      /** user attributes */
      ...(user?.currentLoyaltyTier?.definition?.title && {
        frequent_foodies_tier: user.currentLoyaltyTier.definition.title,
      }),
      ...(user?.created && { user_signup_date: dayjs(user.created).format('YYYY-MM-DD') }),
      ...(user?.confirmedBookingsCount !== undefined && {
        user_has_booked_before: user.confirmedBookingsCount > 0,
        user_total_bookings_made: user.confirmedBookingsCount,
      }),
      ...(user?.lastBookingDate && {
        user_last_booking_date: dayjs(user.lastBookingDate).format('YYYY-MM-DD'),
      }),
      ...(pointsEarned !== undefined && {
        frequent_foodies_points_balance_last_12_months: pointsEarned,
      }),
      ...(user?.customerValueNZD && {
        user_total_booking_spend_in_nzd: user.customerValueNZD.split(' ')[0],
      }),
      ...getCreditSpentAttributes(),
    }),
  };
  return modifiedProps;
};

interface GA4Items {
  [key: string]: string[] | string | undefined;
}

const reformatProps = (props) => {
  let reformattedProps = { ...props };

  if (
    reformattedProps.date_time &&
    reformattedProps.date_time !== 'any' &&
    !Number.isNaN(Date.parse(reformattedProps.date_time)) // Try to make sure the date is valid/safe
  ) {
    const dateTimeObj = new Date(reformattedProps.date_time);
    const dateTimeLocalObj = new Date(
      dateTimeObj.getTime() - dateTimeObj.getTimezoneOffset() * 60000
    );
    reformattedProps = {
      ...reformattedProps,
      date: dateTimeLocalObj.toISOString().split('T')[0],
      time: dateTimeLocalObj.toISOString().split('T')[1],
      date_utc: dateTimeObj.toISOString().split('T')[0],
      time_utc: dateTimeObj.toISOString().split('T')[1],
    };
  }
  if (reformattedProps.date_time === 'any') {
    reformattedProps.date = 'any';
    reformattedProps.time = 'any';
    reformattedProps.date_utc = 'any';
    reformattedProps.time_utc = 'any';
  }
  if (
    reformattedProps.booking_selected_date_time &&
    reformattedProps.booking_selected_date_time !== 'any' &&
    !Number.isNaN(Date.parse(reformattedProps.booking_selected_date_time)) // Try to make sure the date is valid/safe
  ) {
    const dateTimeObj = new Date(reformattedProps.booking_selected_date_time);
    const dateTimeLocalObj = new Date(
      dateTimeObj.getTime() - dateTimeObj.getTimezoneOffset() * 60000
    );
    reformattedProps = {
      ...reformattedProps,
      booking_selected_date: dateTimeLocalObj.toISOString().split('T')[0],
      booking_selected_time: dateTimeLocalObj.toISOString().split('T')[1],
      booking_selected_date_utc: dateTimeObj.toISOString().split('T')[0],
      booking_selected_time_utc: dateTimeObj.toISOString().split('T')[1],
    };
  }
  if (reformattedProps.booking_selected_date_time === 'any') {
    reformattedProps.booking_selected_date = 'any';
    reformattedProps.booking_selected_time = 'any';
    reformattedProps.booking_selected_date_utc = 'any';
    reformattedProps.booking_selected_time_utc = 'any';
  }
  if (
    reformattedProps.filtered_date_time &&
    reformattedProps.filtered_date_time !== 'any' &&
    !Number.isNaN(Date.parse(reformattedProps.filtered_date_time))
  ) {
    const dateTimeObj = new Date(reformattedProps.filtered_date_time);
    const dateTimeLocalObj = new Date(
      dateTimeObj.getTime() - dateTimeObj.getTimezoneOffset() * 60000
    );
    reformattedProps = {
      ...reformattedProps,
      filtered_date: dateTimeLocalObj.toISOString().split('T')[0],
      filtered_time: dateTimeLocalObj.toISOString().split('T')[1],
      filtered_date_utc: dateTimeObj.toISOString().split('T')[0],
      filtered_time_utc: dateTimeObj.toISOString().split('T')[1],
    };
  }
  if (reformattedProps.filtered_date_time === 'any') {
    reformattedProps.filtered_date = 'any';
    reformattedProps.filtered_time = 'any';
    reformattedProps.filtered_date_utc = 'any';
    reformattedProps.filtered_time_utc = 'any';
  }
  if (reformattedProps.restaurant_cuisines) {
    reformattedProps.restaurant_cuisines = reformattedProps.restaurant_cuisines.edges.map(
      (edge) => {
        let cuisine = null;
        if (edge.node.title) {
          cuisine = edge.node.title;
        }
        if (edge.node.label) {
          cuisine = edge.node.label;
        }
        return cuisine;
      }
    );
  }

  const item: GA4Items = {};
  if (reformattedProps.content_ids) {
    reformattedProps.content_ids = [reformattedProps.content_ids];
    item.item_id = reformattedProps.content_ids.toString();
  }
  if (reformattedProps.content_type) {
    item.item_category = reformattedProps.content_type;
  }
  if (reformattedProps.content_name) {
    item.item_name = reformattedProps.content_name;
  }

  if (Object.keys(item).length > 0) {
    reformattedProps.items = [item];
  }

  return reformattedProps;
};

const modifyProps = (props, canTrack, user) => {
  const totalProps = addCommonEvents(props, user, canTrack);
  const reformattedProps = reformatProps(totalProps);
  const modifiedProps = canTrack ? reformattedProps : hideParameters(reformattedProps);
  return modifiedProps;
};

interface AnalyticsContext {
  [key: string]: string | number | boolean | undefined;
}

interface TrackProps {
  canTrack: boolean;
  getSnaptrUserData: () => SnaptrUserData;
  analyticsContext?: AnalyticsContext;
  user: User;
}
const track =
  ({ canTrack, getSnaptrUserData, analyticsContext, user }: TrackProps) =>
  (eventName: any, props: any): void => {
    const combinedProps = {
      ...(analyticsContext ?? {}),
      ...props,
    };
    const modifiedProps = modifyProps(combinedProps, canTrack, user);
    if (canTrack) {
      trackAmplitudeEvent(eventName, modifiedProps);
    }
    notifyAnalyticsEventListeners(eventName, modifiedProps);

    const createTransactionData = () => ({
      ...(props.transaction_value && { price: props.transaction_value }),
      ...(props.currency && { currency: props.currency }),
      ...(props.content_ids && { item_ids: [props.content_ids] }),
      ...(props.category && { item_category: props.category }),
      quantity: 1,
    });

    const eventHandlers = {
      product_added_to_cart: () => {
        const snaptrTransactionData = createTransactionData();
        gtmEvent('Product_Added_To_Cart', modifiedProps);
        snaptrEvent('ADD_CART', {
          ...getSnaptrUserData(),
          ...snaptrTransactionData,
        });
        metaPixelTrack('AddToCart', modifiedProps);
      },

      purchase: () => {
        const snaptrTransactionData = createTransactionData();
        gtmEvent('purchase', modifiedProps);
        snaptrEvent('PURCHASE', {
          ...getSnaptrUserData(),
          ...snaptrTransactionData,
          ...(props.transaction_id && { transaction_id: props.transaction_id }),
        });
        metaPixelTrack('Purchase', modifiedProps);

        if (props.category === 'booking') {
          openAIAdsMeasure(
            'booking_placed',
            {
              type: 'custom',
              amount: props.transaction_value ?? props.value,
              currency: props.currency,
              contents: [
                {
                  ...(props.content_ids && { id: props.content_ids }),
                  ...(props.content_name && { name: props.content_name }),
                  content_type: 'restaurant_booking',
                  quantity: props.quantity ?? 1,
                },
              ],
            },
            {
              ...(props.transaction_id && { event_id: props.transaction_id }),
            }
          );
        }
      },

      account_created: () => {
        gtmEvent('Account_Created', modifiedProps);
        snaptrEvent('SIGN_UP', {
          ...getSnaptrUserData(),
          ...(props.email && { user_email: props.email }),
          ...(props.phone && { user_phone_number: props.phone }),
        });
        metaPixelCompleteRegistration(modifiedProps);
        podscribePurchase(props.form_email ?? props.email);
        adalyserSignUp();
        openAIAdsMeasure(
          'account_created',
          {
            type: 'custom',
          },
          {
            ...(modifiedProps.event_id && { event_id: modifiedProps.event_id }),
          }
        );
      },

      booking_initialized: () => {
        gtmEvent('Booking_Initialized', modifiedProps);
        metaPixelTrack('InitiateCheckout', modifiedProps);
      },

      booking_status: () => {
        if (props.status === 'Confirmed') {
          gtmEvent('Booking_Status', modifiedProps);
        }
      },
    };

    const handler = eventHandlers[eventName];
    if (handler && canTrack) {
      handler();
    }
  };

interface PageProps {
  canTrack: boolean;
  pageId: object;
  getSnaptrUserData: () => SnaptrUserData;
  user: User;
}
const page =
  ({ canTrack, pageId, getSnaptrUserData, user }: PageProps) =>
  (eventName: any, props: any): void => {
    const combinedProps = { ...props, page_id: pageId, content_ids: pageId };
    const modifiedProps = modifyProps(combinedProps, canTrack, user);
    // Amplitude buffers independently so its startup never delays the other page analytics channels.
    if (canTrack) {
      trackAmplitudeEvent(eventName, modifiedProps);
    }
    notifyAnalyticsEventListeners(eventName, modifiedProps);

    const snaptrUserData = canTrack ? getSnaptrUserData() : {};
    const pageEventHandlers = {
      restaurant_viewed: () => {
        gtmEvent('restaurant_viewed', modifiedProps);
        snaptrEvent('VIEW_CONTENT', {
          ...snaptrUserData,
          ...(props.restaurant_id && { item_ids: [props.restaurant_id] }),
          ...(props.currency && { currency: props.currency }),
          item_category: 'restaurant',
        });
        metaPixelTrack('ViewContent', {
          ...modifiedProps,
          ...(props.restaurant_id && { content_ids: props.restaurant_id }),
          ...(props.restaurant_name && { content_name: props.restaurant_name }),
          content_type: 'restaurant',
        });
      },

      list_viewed: () => {
        gtmEvent('list_viewed', modifiedProps);
      },
    };

    if (canTrack) {
      snaptrEvent('PAGE_VIEW', snaptrUserData);
      metaPixelPageView(modifiedProps);

      const pageHandler = pageEventHandlers[eventName];
      if (pageHandler) {
        pageHandler();
      }
    }
  };

interface IdentifyProps {
  canTrack: boolean;
  user: User;
}
const identify =
  ({ canTrack, user }: IdentifyProps) =>
  (userId: string, traits: any): void => {
    const modifiedTraits = modifyProps(traits, canTrack, user);
    const brazeContactPermission = traits?.contact_permission ?? user?.contactPermission;
    const brazeAllowed = isBrazeAllowed({
      consentAllowed: canTrack,
      contactPermission: brazeContactPermission,
      pushPermissionGranted: isPushNotificationGranted(),
    });

    if (canTrack) {
      identifyAmplitudeUser(userId, modifiedTraits);
    }

    // Braze is communications-only here; other analytics stay gated by canTrack above.
    if (isBrazeInitialized() && brazeAllowed) {
      changeUser(userId);
    }
  };

const groupIdentify =
  ({ canTrack, user }: IdentifyProps) =>
  (groupType: string, groupId: string, traits: any): void => {
    if (!canTrack) {
      return;
    }

    const modifiedTraits = modifyProps(traits, canTrack, user);
    const identifyObj = new amplitude.Identify();
    if (groupType === 'restaurant') {
      identifyObj.add('impressions', 1);
    }
    Object.keys(modifiedTraits).forEach((key) => {
      if (![undefined, null].includes(modifiedTraits[key])) {
        identifyObj.set(key, modifiedTraits[key]);
      }
    });
    runConsentedAmplitudeOperation(() => {
      amplitude.groupIdentify(groupType, groupId, identifyObj);
    });
  };

const trackRevenue =
  (canTrack: boolean) =>
  (props: any): void => {
    if (!canTrack) {
      return;
    }

    const event = new amplitude.Revenue()
      .setProductId(props.product_id.toString())
      .setPrice(props.price)
      .setRevenue(props.revenue)
      .setCurrency(props.currency)
      .setQuantity(1);
    runConsentedAmplitudeOperation(() => {
      amplitude.revenue(event);
    });
  };

export const useAnalytics = () => {
  const { allowed: canTrack } = useConsent();
  const data = useContext(SilverstripeContext);
  const pageId = data?.page?.id;
  const { user } = useAuthState();
  const analyticsContext = useContext(TrackContext);
  // Read advertising cookies when sending an event, not for every consuming component render.
  const getSnaptrUserData = (): SnaptrUserData => {
    if (!canTrack) return {};

    const uuidC1 = getCookie('_scid');
    return {
      ...(uuidC1 && { uuid_c1: uuidC1 }),
      ...(user?.email && { user_email: user.email }),
      ...(user?.phoneNumber && { user_phone_number: user.phoneNumber }),
    };
  };

  return {
    ready: canTrack,
    ...(canTrack ? amplitude : {}),
    track: track({ canTrack, getSnaptrUserData, analyticsContext, user }),
    page: page({ canTrack, pageId, getSnaptrUserData, user }),
    identify: identify({ canTrack, user }),
    groupIdentify: groupIdentify({ canTrack, user }),
    trackRevenue: trackRevenue(canTrack),
    alias: () => {},
    group: () => {},
  };
};
